import { createFileRoute } from "@tanstack/react-router";
import { SEOPage } from "@/components/site/SEOPage";
import { Check, Layers, Monitor, Smartphone, Layout, Settings, Cpu, Zap, Info, ShieldCheck, List, Play } from "lucide-react";

const ArticlePage = () => {
  const breadcrumbs = [
    { label: "Elite", href: "/" },
    { label: "Blog", href: "/blog" },
    { label: "Como Estruturar uma Revenda IPTV", href: "/blog/como-estruturar-revenda-iptv" }
  ];

  const sections = [
    {
      title: "1. A Base: Escolha da Infraestrutura e Painel",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            Estruturar uma revenda de IPTV de sucesso começa com a escolha da base tecnológica. Se o seu servidor cai ou trava constantemente, não há estratégia de marketing no mundo que salve o seu negócio. A infraestrutura ideal deve ser composta por um painel de gestão intuitivo e um servidor com alta redundância.
          </p>
          <p className="text-lg leading-relaxed text-muted-foreground mb-6">
            O painel P2 PRO, por exemplo, permite que você gerencie usuários, gere testes automáticos e acompanhe o consumo de banda em tempo real. Essa visibilidade é o que diferencia uma operação caseira de uma empresa profissional de entretenimento.
          </p>
        </div>
      )
    },
    {
      title: "2. Organização do Fluxo Operacional",
      type: "featured" as const,
      content: (
        <div className="space-y-8">
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><Settings className="w-6 h-6 text-electric" /> Gestão de Testes</h4>
              <p className="text-sm text-muted-foreground">Automatize a entrega de testes. Cada minuto que você gasta gerando um teste manualmente é um minuto perdido que poderia estar sendo usado para fechar vendas ou criar conteúdo.</p>
            </div>
            <div className="bg-black/30 p-8 rounded-3xl border border-white/5 space-y-4">
              <h4 className="text-white text-xl font-bold mb-4 flex items-center gap-2"><List className="w-6 h-6 text-amber-500" /> Controle de Vencimentos</h4>
              <p className="text-sm text-muted-foreground">Use planilhas ou, preferencialmente, o sistema de CRM integrado para saber exatamente quem vence nos próximos 3 dias. A renovação proativa é a alma da recorrência.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "3. Atendimento e Suporte em Camadas",
      content: (
        <div className="prose prose-invert max-w-none space-y-8">
          <p className="text-muted-foreground">Para não ficar sobrecarregado, você precisa de um sistema de atendimento eficiente:</p>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">1</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Base de Conhecimento (Autoatendimento)</h4>
              <p className="text-muted-foreground">Crie vídeos curtos e PDFs ensinando a configurar o IPTV em cada dispositivo (Samsung, LG, Roku, Android). Envie isso assim que o cliente fechar ou pedir o teste.</p>
            </div>
          </div>
          <div className="flex gap-6 items-start">
            <div className="w-12 h-12 rounded-2xl bg-electric/20 text-electric flex items-center justify-center flex-shrink-0 font-black italic">2</div>
            <div>
              <h4 className="text-white text-xl font-bold mb-2">Atendimento de Nível 1 (Suporte Rápido)</h4>
              <p className="text-muted-foreground">Respostas rápidas para problemas simples como "esqueci minha senha" ou "meu app não abre". Tenha essas respostas salvas no teclado do seu celular.</p>
            </div>
          </div>
        </div>
      )
    },
    {
      title: "4. Ferramentas Indispensáveis",
      content: (
        <div className="prose prose-invert max-w-none">
          <p className="text-muted-foreground mb-6">Além do seu painel de revenda, algumas ferramentas externas vão acelerar seu crescimento:</p>
          <ul className="space-y-4">
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">WhatsApp Business com Catálogo</h5>
                <p className="text-sm text-muted-foreground">Mostre os planos e dispositivos compatíveis de forma visual e profissional.</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Check className="w-6 h-6 text-electric mt-1 flex-shrink-0" />
              <div>
                <h5 className="font-bold text-white">Ferramentas de Design (Canva)</h5>
                <p className="text-sm text-muted-foreground">Crie banners de ofertas e tutoriais com a sua identidade visual para passar mais credibilidade.</p>
              </div>
            </li>
          </ul>
        </div>
      )
    }
  ];

  return (
    <div className="relative">
      <SEOPage
        category="Guias IPTV"
        title="Como Estruturar uma Revenda IPTV de Alta Performance"
        subtitle="O passo a passo técnico e estratégico para montar uma operação profissional que aguenta centenas de clientes com baixo esforço manual."
        heroImage="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
        introParagraphs={[
          "Muitos revendedores entram no mercado achando que basta comprar créditos e enviar links. Essa abordagem amadora é o motivo pelo qual 80% desistem no primeiro ano. Para ser lucrativo de verdade, você precisa pensar como uma empresa: estruturar processos, automatizar tarefas repetitivas e focar na experiência do usuário.",
          "Neste guia técnico, vamos mergulhar na estruturação física e digital da sua revenda. Vamos falar sobre a importância de um painel estável, como organizar seu atendimento para não ser escravo do WhatsApp e quais ferramentas são essenciais para quem quer sair da casa dos 10 clientes e chegar aos mil.",
          "A estrutura é o que sustenta o crescimento. Sem ela, quanto mais clientes você tiver, mais problemas você terá. Com uma boa estrutura, mais clientes significam apenas mais lucro e liberdade. Vamos começar a montar a sua."
        ]}
        breadcrumbs={breadcrumbs}
        faq={[
          {
            question: "É melhor focar em quantidade ou qualidade de canais?",
            answer: "Qualidade sempre vence. Clientes ficam frustrados com listas gigantes onde nada funciona. Foque em entregar o que realmente importa (Canais abertos, esportes, filmes e séries) com estabilidade total."
          },
          {
            question: "Como organizar os pagamentos dos clientes?",
            answer: "Utilize chaves PIX copia e cola e, se possível, plataformas de checkout que enviem o comprovante automaticamente para o seu sistema ou e-mail."
          }
        ]}
        benefits={[
          {
            title: "Processos Otimizados",
            description: "Gaste menos tempo com burocracia e mais tempo fechando novos negócios lucrativos.",
            icon: <Zap className="w-6 h-6" />
          },
          {
            title: "Marca Profissional",
            description: "Destaque-se da concorrência amadora com uma identidade visual e atendimento de elite.",
            icon: <ShieldCheck className="w-6 h-6" />
          }
        ]}
        howItWorks={[
          {
            title: "Mapeamento de Processos",
            description: "Defina como será o fluxo desde o primeiro contato do cliente até o pós-venda."
          },
          {
            title: "Implementação de Ferramentas",
            description: "Escolha e configure o painel e os canais de atendimento que sustentarão sua operação."
          }
        ]}
        diferenciais={[
          {
            title: "Tecnologia P2P Avançada",
            description: "A base da nossa estrutura garante que sua revenda tenha a menor taxa de reclamações do mercado.",
            icon: <Cpu className="w-6 h-6" />
          }
        ]}
        sections={sections}
        ctaTitle="🏢 Pronto para Profissionalizar sua Revenda?"
        ctaDescription="Não perca mais tempo com métodos amadores. Utilize a estrutura da P2 PRO para levar seu negócio ao próximo nível de faturamento e estabilidade."
        ctaButtonText="Quero Estruturar minha Revenda Agora"
      />
    </div>
  );
};

export const Route = createFileRoute("/blog/como-estruturar-revenda-iptv")({
  head: () => ({
    meta: [
      { title: "Como Estruturar uma Revenda IPTV de Alta Performance" },
      { name: "description", content: "Guia completo de estruturação para revendedores IPTV. Aprenda a organizar seu painel, automatizar atendimentos e usar ferramentas que escalam seu lucro." },
    ],
  }),
  component: ArticlePage,
});